import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { CATEGORIES } from "@/mocks/comics";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "分类", to: "/category/hot-blood" },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
    setMobileOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? "bg-background-950/92 backdrop-blur-md border-b hairline" : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center gap-4 h-16 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-primary-500 to-secondary-600 text-background-50">
              <i className="ri-book-open-line text-lg md:text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-bold text-[17px] md:text-[19px] tracking-tight text-foreground-50">
                禁漫天堂
              </span>
              <span className="hidden sm:block text-[10px] tracking-[0.28em] text-foreground-400 font-label mt-1">
                MANGA HAVEN
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 ml-4">
            {NAV_ITEMS.map((item) =>
              item.label === "分类" ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setCategoryOpen(true)}
                  onMouseLeave={() => setCategoryOpen(false)}
                >
                  <button
                    type="button"
                    onClick={() => navigate("/category/hot-blood")}
                    className="px-3.5 py-2 rounded-md text-sm text-foreground-200 hover:text-accent-300 hover:bg-background-800/60 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  >
                    分类
                    <i className={`ri-arrow-down-s-line text-sm transition-transform ${categoryOpen ? "rotate-180" : ""}`} />
                  </button>
                  {categoryOpen && (
                    <div className="absolute left-0 top-full pt-2 w-[420px] animate-fade-in">
                      <div className="glass-panel rounded-lg p-3 grid grid-cols-2 gap-1">
                        {CATEGORIES.map((cat) => (
                          <Link
                            key={cat.key}
                            to={`/category/${cat.key}`}
                            className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-300 hover:bg-primary-800/50 hover:text-foreground-50 transition-colors"
                          >
                            <i className={`${cat.icon} text-accent-400`} />
                            <span className="whitespace-nowrap">{cat.name}</span>
                            <span className="ml-auto text-[11px] text-foreground-500">{cat.en}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.to}
                  className={({ isActive }) =>
                    `px-3.5 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${
                      isActive && item.to !== "/search"
                        ? "text-accent-300 bg-background-800/60"
                        : "text-foreground-200 hover:text-accent-300 hover:bg-background-800/60"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ),
            )}
          </nav>

          <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-[360px] ml-auto items-center">
            <div className="relative w-full">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画、作者或题材…"
                aria-label="搜索漫画关键词"
                className="w-full h-9 pl-9 pr-3 rounded-full text-sm bg-background-900/70 border hairline text-foreground-100 placeholder:text-foreground-500 outline-none focus:border-accent-400/70 transition-colors"
              />
            </div>
          </form>

          <div className="hidden lg:flex items-center gap-2 ml-2">
            <Link
              to="/category/fantasy"
              className="h-9 px-4 rounded-md bg-accent-500 text-background-950 text-sm font-medium flex items-center gap-1.5 hover:bg-accent-400 transition-colors whitespace-nowrap"
            >
              <i className="ri-flashlight-line" />
              今日推荐
            </Link>
          </div>

          <button
            type="button"
            aria-label="打开导航菜单"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="lg:hidden ml-auto w-10 h-10 rounded-md flex items-center justify-center text-foreground-100 bg-background-800/70 border hairline cursor-pointer"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-3-line text-xl"} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-950/97 backdrop-blur-md border-t hairline animate-fade-in">
          <div className="px-4 py-4 flex flex-col gap-3">
            <form onSubmit={submitSearch} className="flex items-center gap-2">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
                <input
                  type="search"
                  name="q"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索漫画关键词"
                  aria-label="搜索漫画关键词"
                  className="w-full h-10 pl-9 pr-3 rounded-md text-sm bg-background-900 border hairline text-foreground-100 placeholder:text-foreground-500 outline-none focus:border-accent-400/70"
                />
              </div>
              <button
                type="submit"
                className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
            <div className="grid grid-cols-2 gap-2">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className="h-10 rounded-md flex items-center justify-center text-sm bg-background-900 border hairline text-foreground-200"
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <div>
              <p className="text-[11px] tracking-[0.2em] text-foreground-500 font-label mb-2">分类快捷入口</p>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => (
                  <Link
                    key={cat.key}
                    to={`/category/${cat.key}`}
                    onClick={() => setMobileOpen(false)}
                    className="h-9 px-3 rounded-md flex items-center gap-1.5 text-xs bg-background-900 border hairline text-foreground-300"
                  >
                    <i className={`${cat.icon} text-accent-400`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}